import React, { memo } from 'react'
import { Statistic, Tooltip } from 'antd'
import { StatusBadge, Well } from '@pushly/aqe/lib/components'
import { StatItem, StatItemCustom, StatItemNumeric, StatItemStatus, StatItemValue, StatsWellProps } from './types'
import './stats-well.scss'
import '../styles/stats-well.scss'

// Optional: import CountUp for animated numbers
// Users need to install react-countup if they want animation
let CountUp: any = null
try {
    CountUp = require('react-countup').default
} catch {
    // react-countup not installed, animation won't work
}

/**
 * Type guard for status stat items
 */
const isStatusItem = (item: StatItem): item is StatItemStatus => item.type === 'status'

/**
 * Type guard for custom render stat items
 */
const isCustomItem = (item: StatItem): item is StatItemCustom => item.type === 'custom'

/**
 * Type guard for numeric stat items (uses Ant Statistic)
 */
const isNumericItem = (item: StatItem): item is StatItemNumeric => item.type === 'numeric'

/**
 * Type guard for standard value stat items
 */
const isValueItem = (item: StatItem): item is StatItemValue => !item.type || item.type === 'stat'

/**
 * Formatter for animated count-up numbers
 */
const createCountUpFormatter = (precision?: number) => (value: number) => {
    if (!CountUp) {
        // Fallback if react-countup not installed
        return value.toLocaleString(undefined, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        })
    }
    return <CountUp end={value} separator="," decimals={precision} duration={1.5} preserveValue />
}

/**
 * Renders the content for a single stat item based on its type
 */
const StatItemContent = memo(({ item, disabled }: { item: StatItem; disabled: boolean }) => {
    if (isStatusItem(item)) {
        return <StatusBadge type={item.badgeType ?? 'notification'} status={item.statusName} expanded={true} />
    }

    if (isCustomItem(item)) {
        return <>{item.render()}</>
    }

    if (isNumericItem(item)) {
        const displayValue = disabled ? '--' : item.value
        const formatter = item.animated && !disabled ? createCountUpFormatter(item.precision) : undefined

        // Build suffix with optional subValue
        let suffix = item.suffix
        if (item.subValue !== undefined && !disabled) {
            const formattedSubValue =
                item.subValueFormat === 'percent'
                    ? `${(item.subValue * 100).toFixed(2)}%`
                    : item.subValue.toLocaleString()
            suffix = (
                <>
                    {item.suffix}
                    <span className="stats-well__sub-value">({formattedSubValue})</span>
                </>
            )
        } else if (disabled && item.subValue !== undefined) {
            suffix = (
                <>
                    {item.suffix}
                    <span className="stats-well__sub-value">(--)</span>
                </>
            )
        }

        return (
            <Statistic
                title={item.label}
                value={displayValue}
                prefix={item.prefix}
                suffix={suffix}
                precision={disabled ? undefined : item.precision}
                formatter={formatter}
                className="stats-well__statistic"
            />
        )
    }

    if (isValueItem(item)) {
        const value = disabled ? '--' : item.value
        return (
            <>
                {item.prefix}
                {value}
                {item.suffix}
                {item.subValue !== undefined && (
                    <span className="stats-well__sub-value">({disabled ? '--' : item.subValue})</span>
                )}
            </>
        )
    }

    return null
})

StatItemContent.displayName = 'StatItemContent'

/**
 * Individual stat item component
 */
export const StatItemComponent = memo(
    ({
        item,
        disabled,
        variant,
        loading = false,
    }: {
        item: StatItem
        disabled: boolean
        variant: 'inline' | 'cards'
        loading?: boolean
    }) => {
        // Status items and items with neverDisabled are never disabled
        const isDisabled = isStatusItem(item) || item.neverDisabled ? false : item.disabled ?? disabled
        const itemClassName = [
            'stats-well__item',
            isDisabled && 'stats-well__item--disabled',
            isNumericItem(item) && 'stats-well__item--numeric',
            item.className,
        ]
            .filter(Boolean)
            .join(' ')

        // For numeric items, use Statistic's built-in title
        const content = isNumericItem(item) ? (
            <div className={itemClassName}>
                <StatItemContent item={item} disabled={isDisabled} />
            </div>
        ) : (
            <div className={itemClassName}>
                <div className="stats-well__label">{item.label}</div>
                <div className="stats-well__value">
                    <StatItemContent item={item} disabled={isDisabled} />
                </div>
            </div>
        )

        // Wrap in Well for cards variant
        if (variant === 'cards') {
            return (
                <Well padding="small" hideHeader footerMode="hidden" loading={loading}>
                    {item.tooltip ? <Tooltip title={item.tooltip}>{content}</Tooltip> : content}
                </Well>
            )
        }

        // Inline variant - just the content with optional tooltip
        return item.tooltip ? <Tooltip title={item.tooltip}>{content}</Tooltip> : content
    },
)

StatItemComponent.displayName = 'StatItemComponent'

/**
 * StatsWell - A flexible stats display component for detail pages
 *
 * Supports two layout variants:
 * - `inline`: Stats displayed in a single row within one Well (default)
 * - `cards`: Each stat displayed as a separate card/Well
 *
 * Responsive behavior is handled via CSS.
 */
export const StatsWell = memo(
    ({ loading = false, disabled = false, items, variant = 'inline', gap, className }: StatsWellProps) => {
        const visibleItems = items.filter((item) => !item.hidden)

        const containerClassName = ['stats-well', `stats-well--${variant}`, className].filter(Boolean).join(' ')

        const itemsStyle = gap ? { gap: `${gap}px` } : undefined

        // Cards variant - no outer Well, each item is its own Well
        if (variant === 'cards') {
            return (
                <div className={containerClassName}>
                    <div className="stats-well__items" style={itemsStyle}>
                        {visibleItems.map((item) => (
                            <StatItemComponent
                                key={item.key}
                                item={item}
                                disabled={disabled}
                                variant={variant}
                                loading={loading}
                            />
                        ))}
                    </div>
                </div>
            )
        }

        // Inline variant - single Well containing all items
        return (
            <Well className={containerClassName} padding="small" hideHeader footerMode="hidden" loading={loading}>
                <div className="stats-well__items" style={itemsStyle}>
                    {visibleItems.map((item) => (
                        <StatItemComponent
                            key={item.key}
                            item={item}
                            disabled={disabled}
                            variant={variant}
                            loading={loading}
                        />
                    ))}
                </div>
            </Well>
        )
    },
)

StatsWell.displayName = 'StatsWell'
